import type {SuccessPageTimelineStep} from '@/components/common/success-page/SuccessPageTimeline'
import {routeTo} from '@/utils/router'

export const getRegistrationFormContent = () => {
  return {
    metaTitle: 'forms.registration-meta-title',
    metaDescription: 'forms.registration-meta-desc',
    headerLabel: 'forms.registration-header-label',
    markets: {
      title: 'forms.registration-markets-selection-title',
      allMarketsTitle: 'forms.registration-more-markets-title',
      allMarketsDescription: 'forms.registration-more-markets-desc',
      allMarketsEmailSubject: 'forms.registration-more-markets-email-subject',
      allMarketsEmailBody: 'forms.registration-more-markets-email-body',
      continueButtonText: 'forms.button-continue-to-goods-and-services',
      interchangeableTrademarkTitle: 'offering-card-interchangeable-trademark',
    },
    monitoringUpsell: {
      label: 'legal-services-brand-monitoring-title',
      title: 'forms.registration-monitoring-upsell-title',
      imageSrc: '/images/icons/brand-protection-shield.svg',
      imageAlt: 'Brand protection shield',
      removedMessage: 'forms.registration-monitoring-upsell-removed',
      subscribeButtonText: 'forms.registration-monitoring-upsell-subscribe-button',
      consentNote: {
        general: {
          note: 'forms.payment-step-consent-note-3',
          linkHrefs: [routeTo.termsAndConditions(), routeTo.accountTermsAndConditions()],
        },
        us: {
          note: 'forms.payment-step-consent-note-4',
          linkHrefs: [
            routeTo.termsAndConditions(),
            routeTo.accountTermsAndConditions(),
            routeTo.engagementLetter(),
          ],
        },
      },
      discountRemoved: 'forms.discount-removed-brand-protection',
      registrationDiscountTitle: 'forms.registration-discount-title',
      monitoringDiscountTitle: 'forms.monitoring-discount-title',
    },
    invoiceDetails: {
      personalInfoTitle: 'forms.applicant-personal-info-title',
      companyRepresentativeInfoTitle: 'forms.company-representative-info-title',
      firstNameLabel: 'contact-name',
      firstNamePlaceholder: 'contact-name-placeholder',
      lastNameLabel: 'contact-surname',
      lastNamePlaceholder: 'contact-surname-placeholder',
      phoneNumberLabel: 'contact-phone',
      phoneNote: 'verification-contact-sms-desc',
      emailLabel: 'contact-email',
      emailPlaceholder: 'contact-email-placeholder',
      contactAddressTitle: 'forms.applicant-address-title',
      addressLabel: 'forms.contact-address',
      streetLabel: 'forms.contact-address-street',
      countryLabel: 'forms.contact-address-country',
      countryPlaceholder: 'forms.contact-address-country-search',
      stateLabel: 'forms.contact-address-state',
      statePlaceholder: 'forms.contact-address-state-search',
      stateOfIncorporationLabel: 'forms.contact-address-state-of-incorporation',
      stateOfIncorporationPlaceholder: 'forms.contact-address-state-of-incorporation-placeholder',
      countryOfIncorporationLabel: 'forms.contact-address-country-of-incorporation',
      countryOfIncorporationPlaceholder:
        'forms.contact-address-country-of-incorporation-placeholder',
      businessInfoTitle: 'forms.applicant-business-info-title',
      businessNameLabel: 'forms.business-name',
      companyIdLabel: 'forms.company-id',
      vatNumberLabel: 'forms.vat-number',
      birthdateLabel: 'forms.contact-birth-date',
      nationalityLabel: 'forms.contact-nationality',
      personalNumberLabel: 'forms.contact-personal-number',
      businessAddressTitle: 'forms.applicant-business-address-title',
      billingInfoTypeTitle: 'forms.billing-info-type-title',
      billingInfoCurrencyChangeText: 'forms.billing-info-currency-change',
      continueButtonText: 'forms.button-continue-to-payment',
    },
    goodsAndServices: {
      title: 'forms.registration-gs-selection-title',
      description: 'forms.registration-gs-selection-desc',
      helpRequiredOptionTitle: 'forms.registration-gs-help-required-option-title',
      helpRequiredOptionSubtitle: 'forms.registration-gs-help-required-option-subtitle',
      helpRequiredOptionDescription: 'forms.registration-gs-help-required-option-desc',
      noHelpRequiredOptionTitle: 'forms.registration-gs-no-help-required-option-title',
      noHelpRequiredOptionSubtitle: 'forms.registration-gs-no-help-required-option-subtitle',
      noHelpRequiredOptionDescription: 'forms.registration-gs-no-help-required-option-desc',
      continueButtonText: 'forms.button-continue-to-invoice-details',
      recommendedBadge: 'forms.registration-gs-recommended-badge',
      recommendedClassesTitle: 'forms.registration-gs-recommended-classes-title',
      recommendedClassesDescription: 'forms.registration-gs-recommended-classes-desc',
      classTitle: 'forms.registration-gs-class-title',
      selectClassesTitle: 'forms.registration-gs-select-classes-title',
      selectClassesButtonText: 'forms.registration-gs-select-classes-button',
      selectedClassesTitle: 'selected-classes-title',
      selectMoreClassesTitle: 'forms.registration-gs-more-classes-title',
      editGsButtonText: 'forms.registration-gs-edit-button',
      gsModalTitle: 'forms.goods-and-services-title',
      noResultsTitle: 'forms.reg-gs-table-no-results',
      includedClassPriceNote: 'forms.included-class-price-note',
      nextExtraClassPriceNote: 'forms.next-class-price-note',
      removeClassConfirmationTitle: 'forms.registration-gs-remove-class-confirmation-title',
      removeClassConfirmationDescription: 'forms.registration-gs-remove-class-confirmation-desc',
      searchPlaceholder: 'search-text',
      removeButtonText: 'button-remove',
      cancelButtonText: 'button-cancel',
      doneButtonText: 'button-done',
    },
    useInCommerce: {
      title: 'forms.registration-gs-us-use-in-commerce-title',
      description: 'forms.registration-gs-us-use-in-commerce-desc',
      intentToUseDescription: 'forms.registration-gs-us-intent-to-use-desc',
      invoiceIssuedTitle: 'forms.registration-gs-us-invoice-issued-title',
      invoiceIssuedDescription: 'forms.registration-gs-us-invoice-issued-desc',
      optionYes: 'option-yes',
      optionNo: 'option-no',
    },
    loadingTitle: 'loading-payment-details',
  }
}

const successRegistrationTimeline: SuccessPageTimelineStep[] = [
  {
    title: 'forms.registration-success-timeline-step-0',
    status: 'completed',
  },
  {
    title: 'forms.registration-success-timeline-step-1',
    status: 'completed',
  },
  {
    title: 'forms.registration-success-timeline-step-2',
    status: 'current',
  },
  {
    title: 'forms.registration-success-timeline-step-3',
    status: 'disabled',
  },
]

const successRegistrationTimelineForBankTransfer: SuccessPageTimelineStep[] = [
  {
    title: 'forms.registration-success-timeline-step-0',
    status: 'completed',
  },
  {
    title: 'forms.registration-success-timeline-step-1',
    status: 'completed',
  },
  {
    title: 'forms.registration-success-timeline-step-bank-transfer-2-0',
    status: 'current',
  },
  {
    title: 'forms.registration-success-timeline-step-bank-transfer-2-1',
    status: 'disabled',
  },
  {
    title: 'forms.registration-success-timeline-step-3',
    status: 'disabled',
  },
]

export const getSuccessRegistrationContent = (isBankTransferPayment?: boolean) => ({
  timelineSteps: isBankTransferPayment
    ? successRegistrationTimelineForBankTransfer
    : successRegistrationTimeline,
  cpButtonText: 'success-page-application-detail-button',
})

export const getSpecifyGoodsAndServicesContent = () => ({
  metaTitle: 'specify-gs-meta-title',
  metaDescription: 'specify-gs-meta-desc',
  headerLabel: 'specify-gs-header-label',
  title: 'specify-gs-title',
  description: 'specify-gs-desc',
  helperText: 'specify-gs-helper',
  examples: [
    {
      title: 'specify-gs-wrong-examples',
      list: [
        'verification-gs-tips-category-0-bad-example-0',
        'verification-gs-tips-category-1-bad-example-0',
        'verification-gs-tips-category-1-bad-example-2',
      ],
    },
    {
      title: 'specify-gs-correct-examples',
      list: [
        'verification-gs-tips-category-0-good-example-0',
        'verification-gs-tips-category-1-good-example-0',
        'verification-gs-tips-category-1-good-example-2',
      ],
    },
  ],
  gsInputPlaceholder: 'specify-gs-input-placeholder',
  manufactureTitle: 'specify-gs-manufacture-title',
  manufactureHelperText: 'specify-gs-manufacture-helper',
  sellTitle: 'specify-gs-sell-title',
  sellHelperText: 'specify-gs-sell-helper',
  optionYes: 'option-yes',
  optionNo: 'option-no',
  buttonText: 'button-send',
})
